import classNames from "classnames";
import { useFeature } from "flagged";
import React from "react";
import { useTranslation } from "react-i18next";

import style from "./side-panel-daily-quota.scss";
import RefreshIcon from "components/icons/RefreshIcon";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Tooltip from "components/tooltip/Tooltip";
import { FLAG_ASSET_MANAGEMENT } from "services/feature/FeatureFlagService";
import { Action, Category, Label, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { QuotaType, workflowService } from "services/workflows/WorkflowService";
import { useTheme } from "utils/useTheme";

import outlinedInfo from "assets/images/icons/outlinedInfo.svg";

import testIds from "testIds.json";

interface RequiredData {
    total: number;
    used: number;
}
interface QuotaItem {
    key: QuotaType;
    label: string;
    translationKey?: string;
    showTooltip?: boolean;
}

export function SidePanelDailyQuota(): JSX.Element | null {
    const { t } = useTranslation();
    const theme = useTheme();
    type QuotaMap = {
        [quota_type: string]: RequiredData;
    };
    const isAssetManagementEnabled = useFeature(FLAG_ASSET_MANAGEMENT);

    const reportQueryActions: QuotaItem[] = [
        {
            key: QuotaType.LOAD_REPORT,
            label: "Load report",
        },
        {
            key: QuotaType.SEARCH_REPORTS,
            label: "Search reports",
        },
        {
            key: QuotaType.FOR_EACH,
            label: "For each",
        },
    ];

    const assetActions: QuotaItem[] = isAssetManagementEnabled
        ? [
              {
                  key: QuotaType.SEARCH_ASSETS,
                  label: "Search Assets",
              },
              {
                  key: QuotaType.LOAD_ASSET,
                  label: "Load Asset",
              },
              {
                  key: QuotaType.SAVE_ASSET,
                  label: "Save Asset",
              },
              { key: QuotaType.GRADE_ASSET, label: "Grade Asset" },
          ]
        : [];

    const QUOTA_ITEMS: QuotaItem[] = [
        {
            key: QuotaType.CONCURRENT_EXECUTIONS,
            label: "Concurrent Executions",
            translationKey: "WorkflowsTable.sidePanel.tabs.dailyQuota.runningWorkflows",
            showTooltip: true,
        },
        {
            key: QuotaType.WORKFLOW_EXECUTIONS,
            label: "Workflow Executions",
            translationKey: "WorkflowsTable.sidePanel.tabs.dailyQuota.executionsPerTenant",
        },
        {
            key: QuotaType.CONDITION,
            label: "Condition",
        },
        ...reportQueryActions,
        {
            key: QuotaType.SET_VALUE,
            label: "Set variable",
        },
        {
            key: QuotaType.SERVER_MESSAGE,
            label: "Server message",
        },
        {
            key: QuotaType.SEND_EMAIL,
            label: "Send email",
        },
        ...assetActions,
    ];

    const [quotaData, setQuotaData] = React.useState<QuotaMap>(Object.create(null));
    const [loading, setLoading] = React.useState(false);
    const [requestFailureMessage, setRequestFailureMessage] = React.useState("");
    const fetchData = (abortController: AbortController) => {
        setLoading(true);
        workflowService
            .fetchQuotas(abortController)
            .then((data) => {
                const quotaMap: QuotaMap = data.quotas.reduce((acc, quota) => {
                    acc[quota.quota_type] = quota;
                    return acc;
                }, {} as QuotaMap);
                setQuotaData(quotaMap);
                setLoading(false);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("WorkflowsTable.sidePanel.logsRequestFailureMessage"));
                }
            });
    };

    const renderQuotaValue = (quotaType: QuotaType) => {
        if (!quotaData[quotaType]) {
            return null;
        }
        const { total, used } = quotaData[quotaType];
        return `${used}/${total}`;
    };

    const renderQuotaItem = (item: QuotaItem, index: number) => {
        const containerClass = classNames(style.container, index === 1 && style.footerSpace);

        return (
            <div key={item.key} className={containerClass}>
                <div>{item.translationKey ? t(item.translationKey) : item.label}</div>
                <div className={item.showTooltip ? style.iconTooltip : undefined}>
                    {item.showTooltip && (
                        <Tooltip content={t("WorkflowsTable.sidePanel.tabs.dailyQuota.infoTooltip")}>
                            <div>
                                <img src={outlinedInfo} alt="status info" />
                            </div>
                        </Tooltip>
                    )}
                    <div>{renderQuotaValue(item.key)}</div>
                </div>
            </div>
        );
    };

    React.useEffect(() => {
        const abortController = new AbortController();
        fetchData(abortController);
        return () => {
            abortController.abort();
        };
    }, []);

    return (
        <>
            {loading ? (
                <LoadingIndicator />
            ) : (
                <>
                    <div className={style.header}>
                        <div className={style.headeritem}>
                            {t("WorkflowsTable.sidePanel.tabs.dailyQuota.title.quotaType")}
                        </div>
                        <div className={style.headeritem}>
                            <Tooltip content={t("WorkflowsTable.sidePanel.tabs.dailyQuota.refreshTooltip")}>
                                <div
                                    onClick={() => {
                                        fetchData(new AbortController());
                                        usageStatisticsService.sendEvent({
                                            label: Label.WORKFLOW_SIDEPANEL,
                                            action: Action.REFRESH_QUOTAS,
                                            category: Category.WORKFLOW,
                                        });
                                    }}
                                    className={style.refreshButton}
                                    data-testid={testIds.workArea.workflows.sidePanel.tabs.dailyQuota.refreshButton}
                                >
                                    <RefreshIcon color={theme.iconFillColor} />
                                </div>
                            </Tooltip>
                            {t("WorkflowsTable.sidePanel.tabs.dailyQuota.title.availablity")}
                        </div>
                    </div>

                    {QUOTA_ITEMS.slice(0, 2).map((item, index) => renderQuotaItem(item, index))}
                    <div className={style.pageFooter} />
                    <div className={style.activityTitle}>
                        {t("WorkflowsTable.sidePanel.tabs.dailyQuota.title.WorkflowEditorActivities")}
                    </div>
                    {QUOTA_ITEMS.slice(2).map((item, index) => renderQuotaItem(item, index + 2))}
                </>
            )}
            {requestFailureMessage && <div>{requestFailureMessage}</div>}
        </>
    );
}
